import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Card,
  CardContent,
  Button,
  Chip,
  Box,
  Grid,
  CircularProgress,
  Container,
  Alert,
  Divider,
  AlertTitle,
  Tooltip,
  Paper,
  IconButton,
} from "@mui/material";
import {
  CreditCard,
  Lock,
  CheckCircle,
  Cancel,
  ArrowBack as ArrowBackIcon,
  Payment,
  Pending,
  HourglassEmpty,
  Autorenew,
  ArrowRight,
  HeadsetMic,
} from "@mui/icons-material";
import DashboardHeader from "../components/headers/DashboardHeader";
import {
  detail_addon_form,
  detail_user_form,
  get_client_fees_api,
} from "../DAL/form/userForm";
import { one_time_payment_with_stripe_api } from "../DAL/form/userForm";
import { useSnackbar } from "notistack";
import { useAdminContext } from "../hooks/UseAdminContext";
import PaidImage from "../components/assets/images/paid.png";
import PendingImage from "../components/assets/images/pending.png";

const FeatureItem = ({ text }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "12px",
      marginBottom: "12px",
    }}
  >
    <div
      style={{
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        backgroundColor: "rgba(21, 151, 138, 0.1)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <CheckCircle
        sx={{
          fontSize: 14,
          color: "#15978a",
        }}
      />
    </div>
    <p className="mb-0">{text}</p>
  </div>
);

const Payments = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { addon_id } = useParams();
  const [initialLoading, setInitialLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [feesData, setFeesData] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [isInvestmentProcessing, setIsInvestmentProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("card");
  const [investmentPaymentMethod, setInvestmentPaymentMethod] =
    useState("card");
  const { adminSetting } = useAdminContext();

  const paymentOptions = [
    { label: "Credit Card", value: "card" },
    { label: "Wire Transfer", value: "wire" },
  ];

  const getPrimaryUserDetail = async () => {
    const result = await detail_user_form();
    if (result.code === 200) {
      const fullForm = result?.form?.form;
      const housingSection = fullForm.find(
        (section) => section.value === "housing_option"
      );
      if (housingSection) {
        setFormData((prev) => ({
          ...prev,
          form: [...prev.form, housingSection],
        }));
      }
    } else {
      console.error(result.message);
    }
  };

  const handleFetchDetail = async () => {
    setInitialLoading(true);
    try {
      const result = addon_id
        ? await detail_addon_form(addon_id)
        : await detail_user_form();
      if (result.code === 200) {
        setFormData(result?.form || {});
        if (addon_id) {
          getPrimaryUserDetail();
        }
      } else {
        enqueueSnackbar(result.message, {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error loading payment data", { variant: "error" });
    } finally {
      setInitialLoading(false);
    }
  };

  const handleApplicationFeePayment = async () => {
    setIsProcessing(true);
    try {
      const postData = {
        payment_type: "initial",
        payment_method: paymentMethod,
      };

      if (addon_id) {
        postData.addon_id = addon_id;
        postData.is_addon = true;
      }

      const result = await one_time_payment_with_stripe_api(postData);
      if (result.code === 200) {
        window.location.assign(result.payment_intent.url); // Fix for mac navigation issue - shamroz
        console.log(result);
      } else {
        enqueueSnackbar(result.message || "Payment failed", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error processing payment", { variant: "error" });
    } finally {
      setIsProcessing(false);
    }
  };
  const handleInvestmentFeePayment = async () => {
    setIsInvestmentProcessing(true);
    try {
      const postData = {
        payment_type: "investment",
        payment_method: investmentPaymentMethod,
      };

      if (addon_id) {
        postData.addon_id = addon_id;
        postData.is_addon = true;
      }

      const result = await one_time_payment_with_stripe_api(postData);
      if (result.code === 200) {
        window.location.assign(result.payment_intent.url); // fix for mac navigation issue - shamroz
      } else {
        enqueueSnackbar(result.message || "Payment failed", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar("Error processing payment", { variant: "error" });
    } finally {
      setIsInvestmentProcessing(false);
    }
  };

  const getApplicationFeeStatus = () => {
    if (!areAllSectionsCompleted()) {
      return {
        icon: <Lock sx={{ color: "#9e9e9e", fontSize: 20 }} />,
        label: "Locked",
        color: "default",
        variant: "outlined",
      };
    }
    const status = formData?.initial_payment_status;
    switch (status) {
      case "paid":
        return {
          icon: <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />,
          label: "Paid",
          color: "success",
          variant: "outlined",
        };
      case "processing":
        return {
          icon: <Lock sx={{ color: "#9e9e9e", fontSize: 20 }} />,
          label: "Processing",
          color: "warning",
          variant: "outlined",
        };
      case "failed":
        return {
          icon: <Cancel sx={{ color: "#f44336", fontSize: 20 }} />,
          label: "Failed",
          color: "error",
          variant: "outlined",
        };
      default:
        return {
          icon: <Payment sx={{ color: "inherit", fontSize: 20 }} />,
          label: "Not Paid",
          color: "error",
          variant: "outlined",
        };
    }
  };

  const getInvestmentFeeStatus = () => {
    const status = formData?.investment_payment_status;
    const formStatus = formData?.form_status;
    const isApproved =
      formStatus === "approved" || formStatus === "fee_pending";

    if (!isApproved) {
      return {
        icon: <Lock sx={{ color: "#9e9e9e", fontSize: 20 }} />,
        label: "Locked",
        color: "default",
        variant: "outlined",
        description: "Available After Approval",
      };
    }

    switch (status) {
      case "paid":
        return {
          icon: <CheckCircle sx={{ color: "#4caf50", fontSize: 20 }} />,
          label: "Paid",
          color: "success",
          variant: "outlined",
        };
      case "processing":
        return {
          icon: <Lock sx={{ color: "#9e9e9e", fontSize: 20 }} />,
          label: "Processing",
          color: "warning",
          variant: "outlined",
        };
      case "failed":
        return {
          icon: <Cancel sx={{ color: "#f44336", fontSize: 20 }} />,
          label: "Failed",
          color: "error",
          variant: "outlined",
        };
      default:
        return {
          icon: <Payment sx={{ color: "inherit", fontSize: 20 }} />,
          label: "Not Paid",
          color: "error",
          variant: "outlined",
        };
    }
  };

  // Check if investment fee section should be enabled
  const isInvestmentFeeEnabled = () => {
    const formStatus = formData?.form_status;
    return (
      (formStatus === "approved" || formStatus === "fee_pending") &&
      adminSetting.enable_investment_card
    );
  };

  // Check if all sections are completed for application fee
  const areAllSectionsCompleted = () => {
    const formSections = formData?.form;
    if (!Array.isArray(formSections)) return false;

    return formSections.every((section) => {
      if (
        section.has_sub_sections &&
        Array.isArray(section.sub_sections) &&
        section.sub_sections.length > 0
      ) {
        return section.sub_sections.every((sub) => sub.is_completed);
      }
      return section.is_completed;
    });
  };

  const getFeesStructure = async () => {
    const result = await get_client_fees_api();
    if (result.code === 200) {
      setFeesData(result.client_fees);
    } else {
      console.error(result.message);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("code-verified")) {
      navigate("/login", { replace: true });
    } else {
      handleFetchDetail();
      getFeesStructure();
    }
  }, []);

  if (initialLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <CircularProgress color="primary" />
      </div>
    );
  }

  const applicationFeeStatus = getApplicationFeeStatus();
  const investmentFeeStatus = getInvestmentFeeStatus();
  const isInvestmentEnabled = isInvestmentFeeEnabled();
  const allSectionsCompleted = areAllSectionsCompleted();

  return (
    <div style={{ minHeight: "100vh" }}>
      <div className="header-container">
        <DashboardHeader />
      </div>
      <div className="container" style={{ maxWidth: "940px" }}>
        <div className="row mt-3 pb-4">
          <div className="col-2 text-start">
            <IconButton onClick={() => navigate("/dashboard")}>
              <ArrowBackIcon sx={{ fontSize: 20, color: "#15978a" }} />
            </IconButton>
          </div>
          <div className="col-8 text-center">
            <h2 className="primary-color">Payments</h2>
            {(addon_id && formData?.addon_info) ||
            formData?.is_addon === false ? (
              <>
                <div className="my-1">
                  <p className="mb-0">
                    Processing payment for{" "}
                    {addon_id && formData?.addon_info
                      ? "add-on applicant"
                      : "primary applicant"}
                    <span className="ms-1">
                      case number (
                      <span className="fw-600 primary-color">
                        {formData?.case_number}
                      </span>
                      )
                    </span>
                  </p>
                </div>
              </>
            ) : null}
          </div>
          <div className="col-2"></div>
          <div className="row mt-3">
            <div className="col-12 text-center mb-4">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: { xs: 2, sm: 3 },
                  flexDirection: { xs: "column", sm: "row" },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor:
                        formData?.initial_payment_status === "paid"
                          ? "#15978a"
                          : "#d1d5db",
                      color:
                        formData?.initial_payment_status === "paid"
                          ? "white"
                          : "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    1
                  </div>
                  <p
                    className="mb-0"
                    style={{
                      color:
                        formData?.initial_payment_status === "paid"
                          ? "#15978a"
                          : "#6b7280",
                    }}
                  >
                    Application Fee
                  </p>
                </Box>

                <Box
                  className="d-none d-sm-block"
                  sx={{
                    width: { xs: "2px", sm: 40 },
                    height: { xs: 40, sm: 2 },
                    backgroundColor: "#d1d5db",
                    mx: { xs: 0, sm: 2 },
                    my: { xs: 1, sm: 0 },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <div
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: "50%",
                      backgroundColor:
                        formData?.initial_payment_status === "paid"
                          ? "#15978a"
                          : "#d1d5db",
                      color:
                        formData?.initial_payment_status === "paid"
                          ? "white"
                          : "#6b7280",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    2
                  </div>
                  <p
                    className="mb-0"
                    style={{
                      color:
                        formData?.initial_payment_status === "paid"
                          ? "#15978a"
                          : "#6b7280",
                    }}
                  >
                    Investment Fee
                  </p>
                </Box>
              </Box>
            </div>
          </div>

          <div className="col-12">
            {!allSectionsCompleted && (
              <Alert
                severity="info"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "14px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <span>
                    Complete your application form first before making the
                    processing fee payment.
                  </span>
                </Box>
              </Alert>
            )}

            {!isInvestmentEnabled && formData?.form_status === "fee_pending" ? (
              <Alert
                severity="success"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "14px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <span>
                    Processing fee payment successful! Your application is now
                    under review.{" "}
                    {formData?.form_status === "fee_pending"
                      ? "Investment fee payment is now available."
                      : "You will be notified once approved, then the investment fee payment will become available."}
                  </span>
                </Box>
              </Alert>
            ) : null}

            {/* Alert for processing fee under verification */}
            {applicationFeeStatus.label === "Processing" && (
              <Alert
                severity="warning"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  borderRadius: "14px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", flex: 1 }}>
                  <span>
                    Processing fee payment is under verification. Please wait
                    while we confirm your payment.
                  </span>
                </Box>
              </Alert>
            )}
          </div>
          <div className="col-12 col-lg-6 mt-2">
            <Card
              sx={{
                borderRadius: "32px",
                overflow: "hidden",
                border: "1px solid #e5e7eb",
                boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "none",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  margin: "14px",
                  padding: "16px",
                  borderRadius: "24px",
                  background: !allSectionsCompleted
                    ? "linear-gradient(90deg, #dae8e6ff 0%, #cfe8e8ff 100%)"
                    : applicationFeeStatus.label === "Paid"
                    ? "linear-gradient(90deg, #007f79 0%, #15978A 100%)"
                    : applicationFeeStatus.label === "Processing"
                    ? "linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)"
                    : "linear-gradient(90deg, #dae8e6ff 0%, #cfe8e8ff 100%)",
                  position: "relative",
                  overflow: "hidden",
                  color:
                    allSectionsCompleted &&
                    applicationFeeStatus.label === "Paid"
                      ? "#fff"
                      : "#000000c1",
                }}
              >
                {allSectionsCompleted &&
                  applicationFeeStatus.label !== "Paid" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-20%",
                        right: "-10%",
                        width: "150px",
                        height: "150px",
                        background: "rgba(255, 255, 255, 0.25)",
                        borderRadius: "50%",
                        filter: "blur(40px)",
                        zIndex: 0,
                      }}
                    />
                  )}

                <Box
                  sx={{
                    display: { xs: "flex", lg: "none" },
                    justifyContent: "flex-end",
                    mb: 1,
                  }}
                >
                  <Chip
                    icon={applicationFeeStatus.icon}
                    label={applicationFeeStatus.label}
                    color={applicationFeeStatus.color}
                    variant={applicationFeeStatus.variant}
                    sx={{
                      borderRadius: "10px",
                      px: 1,
                      height: "32px",
                      backgroundColor: "#fff",
                      border: "1px solid rgba(0,0,0,0.05)",
                      "& .MuiChip-label": {
                        fontSize: "14px",
                        fontWeight: 600,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <h4
                    style={{
                      color:
                        allSectionsCompleted &&
                        applicationFeeStatus.label === "Paid"
                          ? "#FFFFFF"
                          : "#000000c1",
                      marginBottom: 0,
                    }}
                  >
                    Application & Background Fee
                  </h4>
                  <Box sx={{ display: { xs: "none", lg: "block" } }}>
                    <Chip
                      icon={applicationFeeStatus.icon}
                      label={applicationFeeStatus.label}
                      color={applicationFeeStatus.color}
                      variant={applicationFeeStatus.variant}
                      sx={{
                        borderRadius: "10px",
                        px: 1,
                        height: "32px",
                        backgroundColor: "#fff",
                        border: "1px solid rgba(0,0,0,0.05)",
                        "& .MuiChip-label": {
                          fontSize: "14px",
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <h2
                  style={{
                    fontWeight: 600,
                    color: "inherit",
                    fontSize: "36px",
                    marginBottom: "24px",
                  }}
                >
                  ${Number(feesData?.[`initial_fee`]).toLocaleString()}
                  <span
                    style={{
                      color:
                        allSectionsCompleted &&
                        applicationFeeStatus.label === "Paid"
                          ? "#FFFFFF"
                          : "#000000",
                      marginLeft: "8px",
                      fontWeight: 500,
                      fontSize: "16px",
                    }}
                  >
                    USD
                  </span>
                </h2>

                <Box
                  sx={{
                    height: "64px",
                    mb: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {applicationFeeStatus.label === "Paid" ? (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle
                        sx={{ fontSize: 40, color: "rgba(255,255,255,0.9)" }}
                      />
                    </Box>
                  ) : !allSectionsCompleted ? (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(0,0,0,0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Lock sx={{ fontSize: 32, color: "#9e9e9e" }} />
                    </Box>
                  ) : applicationFeeStatus.label === "Processing" ? (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(217, 119, 6, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <HourglassEmpty
                        sx={{
                          fontSize: 32,
                          color: "#d97706",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      <span
                        style={{
                          color:
                            applicationFeeStatus.label === "Paid"
                              ? "rgba(255,255,255,0.8)"
                              : "#000000c1",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        Payment Method:
                      </span>
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          gap: 1.5,
                          background: "rgba(255,255,255,0.45)",
                          backdropFilter: "blur(10px)",
                          p: 0.5,
                          borderRadius: "12px",
                          border: "1px solid rgb(200 217 215)",
                          mt: 0.5,
                        }}
                      >
                        {paymentOptions.map((option) => (
                          <Button
                            key={option.value}
                            onClick={() => setPaymentMethod(option.value)}
                            sx={{
                              flex: 1,
                              borderRadius: "10px",
                              textTransform: "none",
                              fontSize: "14px !important",
                              fontWeight:
                                paymentMethod === option.value
                                  ? "600 !important"
                                  : "500 !important",
                              backgroundColor:
                                paymentMethod === option.value
                                  ? "#15978a"
                                  : "transparent",
                              color:
                                paymentMethod === option.value
                                  ? "#fff"
                                  : "#1a3a36",
                              boxShadow:
                                paymentMethod === option.value
                                  ? "0 4px 12px rgba(0,0,0,0.1)"
                                  : "none",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                fontWeight: "inherit",
                                fontSize: "14px",
                              }}
                            >
                              {paymentMethod === option.value && (
                                <CheckCircle sx={{ fontSize: 18 }} />
                              )}
                              {option.label}
                            </Box>
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    isProcessing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : !allSectionsCompleted ? (
                      <Lock />
                    ) : applicationFeeStatus.label === "Processing" ? (
                      <Lock />
                    ) : applicationFeeStatus.label === "Paid" ? (
                      <CheckCircle />
                    ) : (
                      <Payment />
                    )
                  }
                  disabled={
                    !allSectionsCompleted ||
                    applicationFeeStatus.label === "Paid" ||
                    applicationFeeStatus.label === "Processing" ||
                    isProcessing
                  }
                  onClick={handleApplicationFeePayment}
                  sx={{
                    py: 1.4,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "16px",
                    backgroundColor:
                      allSectionsCompleted &&
                      applicationFeeStatus.label !== "Paid"
                        ? "#15978a"
                        : applicationFeeStatus.label === "Paid"
                        ? "rgba(255,255,255,0.2)"
                        : "#f3f4f6",
                    color:
                      allSectionsCompleted &&
                      applicationFeeStatus.label !== "Paid"
                        ? "#fff"
                        : applicationFeeStatus.label === "Paid"
                        ? "#fff"
                        : "#9ca3af",

                    border:
                      applicationFeeStatus.label === "Paid"
                        ? "1px solid rgba(255,255,255,0.3)"
                        : "none",
                    "&.Mui-disabled": {
                      backgroundColor:
                        applicationFeeStatus.label === "Paid"
                          ? "rgba(255,255,255,0.2)"
                          : applicationFeeStatus.label === "Processing"
                          ? "rgba(217, 119, 6, 0.1)"
                          : "#f3f4f6",
                      color:
                        applicationFeeStatus.label === "Paid"
                          ? "#fff"
                          : applicationFeeStatus.label === "Processing"
                          ? "#d97706"
                          : "#9ca3af",
                    },
                    "&:hover": {
                      backgroundColor: "#0E6960",
                    },
                  }}
                >
                  {isProcessing
                    ? "Processing..."
                    : !allSectionsCompleted
                    ? "Complete Application First"
                    : applicationFeeStatus.label === "Paid"
                    ? "Payment Completed"
                    : applicationFeeStatus.label === "Processing"
                    ? "Under Verification"
                    : "Pay Now"}
                </Button>
              </Box>

              <Box sx={{ py: 1, px: 4, flexGrow: 1, backgroundColor: "#fff" }}>
                <h4>Payment Details:</h4>

                <FeatureItem text="Secure payment via Stripe" />
                <FeatureItem text="Required before application review" />
                <FeatureItem text="Instant processing & confirmation" />
              </Box>
            </Card>
          </div>
          <div className="col-12 col-lg-6 mt-2">
            <Card
              sx={{
                borderRadius: "32px",
                overflow: "hidden",
                border: "1px solid #e5e7eb",
                boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                transition: "none",
                backgroundColor: "#fff",
              }}
            >
              <Box
                sx={{
                  margin: "14px",
                  padding: "16px",
                  borderRadius: "24px",
                  background: !isInvestmentEnabled
                    ? "linear-gradient(90deg, #cfe8e8ff 0%, #dae8e6ff 100%)"
                    : investmentFeeStatus.label === "Paid"
                    ? "linear-gradient(90deg, #15978A 0%, #007f79 100%)"
                    : investmentFeeStatus.label === "Processing"
                    ? "linear-gradient(90deg, #fef3c7 0%, #fde68a 100%)"
                    : "linear-gradient(90deg, #cfe8e8ff 0%, #dae8e6ff 100%)",
                  position: "relative",
                  overflow: "hidden",
                  color:
                    isInvestmentEnabled && investmentFeeStatus.label === "Paid"
                      ? "#fff"
                      : "#000000c1",
                }}
              >
                {isInvestmentEnabled &&
                  investmentFeeStatus.label !== "Paid" && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "-20%",
                        right: "-10%",
                        width: "150px",
                        height: "150px",
                        background: "rgba(255, 255, 255, 0.25)",
                        borderRadius: "50%",
                        filter: "blur(40px)",
                        zIndex: 0,
                      }}
                    />
                  )}

                <Box
                  sx={{
                    display: { xs: "flex", lg: "none" },
                    justifyContent: "flex-end",
                    mb: 1,
                  }}
                >
                  <Chip
                    icon={investmentFeeStatus.icon}
                    label={investmentFeeStatus.label}
                    color={investmentFeeStatus.color}
                    variant={investmentFeeStatus.variant}
                    sx={{
                      borderRadius: "10px",
                      px: 1,
                      height: "32px",
                      backgroundColor: "#fff",
                      border: "1px solid rgba(0,0,0,0.05)",
                      "& .MuiChip-label": {
                        fontSize: "14px",
                        fontWeight: 600,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 2,
                  }}
                >
                  <h4
                    style={{
                      color:
                        isInvestmentEnabled &&
                        investmentFeeStatus.label === "Paid"
                          ? "#FFFFFF"
                          : "#000000c1",
                      marginBottom: 0,
                    }}
                  >
                    In-Country Investment Fee
                  </h4>
                  <Box sx={{ display: { xs: "none", lg: "block" } }}>
                    <Chip
                      icon={investmentFeeStatus.icon}
                      label={investmentFeeStatus.label}
                      color={investmentFeeStatus.color}
                      variant={investmentFeeStatus.variant}
                      sx={{
                        borderRadius: "10px",
                        px: 1,
                        height: "32px",
                        backgroundColor: "#fff",
                        border: "1px solid rgba(0,0,0,0.05)",
                        "& .MuiChip-label": {
                          fontSize: "14px",
                          fontWeight: 600,
                        },
                      }}
                    />
                  </Box>
                </Box>

                <h2
                  style={{
                    fontWeight: 600,
                    color: "inherit",
                    fontSize: "36px",
                    marginBottom: "24px",
                  }}
                >
                  ${Number(feesData?.[`investment_fee`]).toLocaleString()}
                  <span
                    style={{
                      color:
                        isInvestmentEnabled &&
                        investmentFeeStatus.label === "Paid"
                          ? "#FFFFFF"
                          : "#000000",
                      marginLeft: "8px",
                      fontWeight: 500,
                      fontSize: "16px",
                    }}
                  >
                    USD
                  </span>
                </h2>

                {/* Housing fee Show on Card */}
                {/* {(() => {
                      const housingSection = formData?.form?.find(
                        (s) => s.value === "housing_option"
                      );
                      const housingNeed = housingSection?.questions?.find(
                        (q) => q.created_for === "housing_need"
                      )?.answer;

                      if (housingNeed !== "yes") return null;

                      const housingType = housingSection?.questions?.find(
                        (q) => q.created_for === "housing_type"
                      )?.answer;

                      const housingCategory = housingSection?.questions?.find(
                        (q) => q.created_for === "housing_category"
                      )?.answer;

                      const feeEntry = feesData?.housing_fee_structure?.find(
                        (f) => f.house_type === housingType
                      );

                      const housingPrice = feeEntry
                        ? housingCategory === "luxury"
                          ? feeEntry.luxury_price
                          : feeEntry.standard_price
                        : null;

                      if (housingPrice == null) return null;

                      return (
                        <>
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: 600,
                              color: "#6b7280",
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              mt: 0.5,
                            }}
                          >
                            🏠{" "}
                            {housingType?.charAt(0).toUpperCase() +
                              housingType?.slice(1)}{" "}
                            (
                            {housingCategory?.charAt(0).toUpperCase() +
                              housingCategory?.slice(1)}
                            ):  {housingPrice} USD
                          </Typography>

                          <Typography
                            sx={{
                              mt: 1,
                              pt: 0.75,
                              borderTop: "1px solid #e5e7eb",
                              fontSize: "15px",
                              fontWeight: 600,
                              color: "#6b7280",
                            }}
                          >
                            🏦 Total Amount: {" "}
                            {feesData?.investment_fee + housingPrice} USD
                          </Typography>
                        </>
                      );
                    })()} */}

                <Box
                  sx={{
                    height: "64px",
                    mb: 4,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {investmentFeeStatus.label === "Paid" ? (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(255,255,255,0.2)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <CheckCircle
                        sx={{ fontSize: 40, color: "rgba(255,255,255,0.9)" }}
                      />
                    </Box>
                  ) : !isInvestmentEnabled ? (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(0,0,0,0.05)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Lock sx={{ fontSize: 32, color: "#9e9e9e" }} />
                    </Box>
                  ) : investmentFeeStatus.label === "Processing" ? (
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "50%",
                        backgroundColor: "rgba(217, 119, 6, 0.1)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <HourglassEmpty
                        sx={{
                          fontSize: 32,
                          color: "#d97706",
                        }}
                      />
                    </Box>
                  ) : (
                    <Box sx={{ width: "100%" }}>
                      <span
                        style={{
                          color:
                            investmentFeeStatus.label === "Paid"
                              ? "rgba(255,255,255,0.8)"
                              : "#000000c1",
                          fontSize: "14px",
                          fontWeight: 500,
                        }}
                      >
                        Payment Method:
                      </span>
                      <Box
                        sx={{
                          display: "flex",
                          width: "100%",
                          gap: 1.5,
                          background: "rgba(255,255,255,0.45)",
                          backdropFilter: "blur(10px)",
                          p: 0.5,
                          borderRadius: "14px",
                          border: "1px solid rgb(200 217 215)",
                          mt: 0.5,
                        }}
                      >
                        {paymentOptions.map((option) => (
                          <Button
                            key={option.value}
                            onClick={() =>
                              setInvestmentPaymentMethod(option.value)
                            }
                            sx={{
                              flex: 1,
                              py: 1.25,
                              borderRadius: "10px",
                              textTransform: "none",
                              fontSize: "14px !important",
                              fontWeight:
                                investmentPaymentMethod === option.value
                                  ? "600 !important"
                                  : "500 !important",
                              backgroundColor:
                                investmentPaymentMethod === option.value
                                  ? "#15978a"
                                  : "transparent",
                              color:
                                investmentPaymentMethod === option.value
                                  ? "#fff"
                                  : "#1a3a36",
                              boxShadow:
                                investmentPaymentMethod === option.value
                                  ? "0 4px 12px rgba(0,0,0,0.1)"
                                  : "none",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 1,
                                fontWeight: "inherit",
                                fontSize: "14px",
                              }}
                            >
                              {investmentPaymentMethod === option.value && (
                                <CheckCircle sx={{ fontSize: 18 }} />
                              )}
                              {option.label}
                            </Box>
                          </Button>
                        ))}
                      </Box>
                    </Box>
                  )}
                </Box>

                <Button
                  variant="contained"
                  fullWidth
                  startIcon={
                    isInvestmentProcessing ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : !isInvestmentEnabled ? (
                      <Lock />
                    ) : investmentFeeStatus.label === "Paid" ? (
                      <CheckCircle />
                    ) : investmentFeeStatus.label === "Processing" ? (
                      <Lock />
                    ) : (
                      <Payment />
                    )
                  }
                  disabled={
                    !isInvestmentEnabled ||
                    investmentFeeStatus.label === "Paid" ||
                    investmentFeeStatus.label === "Processing" ||
                    isInvestmentProcessing
                  }
                  onClick={handleInvestmentFeePayment}
                  sx={{
                    py: 1.4,
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "16px",
                    backgroundColor:
                      isInvestmentEnabled &&
                      investmentFeeStatus.label !== "Paid"
                        ? "#15978a"
                        : investmentFeeStatus.label === "Paid"
                        ? "rgba(255,255,255,0.2)"
                        : "#f3f4f6",
                    color:
                      isInvestmentEnabled &&
                      investmentFeeStatus.label !== "Paid"
                        ? "#fff"
                        : investmentFeeStatus.label === "Paid"
                        ? "#fff"
                        : "#9ca3af",

                    border:
                      investmentFeeStatus.label === "Paid"
                        ? "1px solid rgba(255,255,255,0.3)"
                        : "none",
                    "&.Mui-disabled": {
                      backgroundColor:
                        investmentFeeStatus.label === "Paid"
                          ? "rgba(255,255,255,0.2)"
                          : investmentFeeStatus.label === "Processing"
                          ? "rgba(217, 119, 6, 0.1)"
                          : "#f3f4f6",
                      color:
                        investmentFeeStatus.label === "Paid"
                          ? "#fff"
                          : investmentFeeStatus.label === "Processing"
                          ? "#d97706"
                          : "#9ca3af",
                    },
                    "&:hover": {
                      backgroundColor: "#0E6960",
                    },
                  }}
                >
                  {isInvestmentProcessing
                    ? "Processing..."
                    : !isInvestmentEnabled
                    ? "Available After Approval"
                    : investmentFeeStatus.label === "Paid"
                    ? "Payment Completed"
                    : investmentFeeStatus.label === "Processing"
                    ? "Under Verification"
                    : "Pay Now"}
                </Button>
              </Box>
              <Box sx={{ py: 1, px: 4, flexGrow: 1, backgroundColor: "#fff" }}>
                <h4>Payment Details:</h4>

                <FeatureItem text="Secure payment via Stripe" />
                <FeatureItem text="Required only if application is approved" />
                <FeatureItem text="2-3 Business Days Verification" />
              </Box>
            </Card>
          </div>
          <div className="col-12">
            <div className="mt-4">
              <Card
                sx={{
                  borderRadius: "32px",
                  overflow: "hidden",
                  border: "1px solid #dde9e8ff",
                  boxShadow: "0 20px 60px rgba(0,0,0,0.06)",
                  transition: "none",
                  padding: "16px",
                }}
              >
                <CardContent sx={{ padding: "16px" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      gap: { xs: 3, md: 4 },
                    }}
                  >
                    {/* Left side: Information List */}
                    <Box sx={{ flex: 1 }}>
                      <h4 className="d-flex align-items-center gap-2">
                        <CreditCard sx={{ color: "#15978a", fontSize: 22 }} />
                        Payment Information
                      </h4>

                      <div className="d-flex flex-column gap-2">
                        {[
                          "Fees are non-refundable once processed",
                          "Each payment is made through Stripe's secure gateway",
                          "Use the same email address as on your application for proper tracking",
                        ].map((text, idx) => (
                          <div
                            key={idx}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <ArrowRight sx={{ color: "#15978a" }} />

                            <p className="mb-0">{text}</p>
                          </div>
                        ))}
                      </div>
                    </Box>

                    {/* Divider for desktop view */}
                    <Divider
                      orientation="vertical"
                      flexItem
                      sx={{ display: { xs: "none", md: "block" } }}
                    />
                    <Divider
                      orientation="horizontal"
                      sx={{ display: { xs: "block", md: "none" } }}
                    />

                    {/* Right side: Payment Support */}
                    <Box sx={{ minWidth: { md: "300px" } }}>
                      <h4 className="d-flex align-items-center gap-2">
                        <HeadsetMic sx={{ color: "#15978a", fontSize: 22 }} />
                        Payment Support
                      </h4>

                      <p>Need help with your payment? Our team is here.</p>

                      <span>
                        Email:{" "}
                        <a
                          href="mailto:support@littlehaitiglobal.com"
                          style={{
                            color: "#15978a",
                            textDecoration: "none",
                            fontWeight: 600,
                            transition: "text-decoration 0.2s",
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.textDecoration = "underline";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.textDecoration = "none";
                          }}
                        >
                          support@littlehaitiglobal.com
                        </a>
                      </span>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payments;
